import { useCallback, useSyncExternalStore } from 'react'

export type CountDraft = {
  stockItemId: string
  sku: string
  barcode: string
  name: string
  countedQuantity: number
  systemQuantityAtScan: number
  note: string
}

const STORAGE_PREFIX = 'helper-count-drafts:'

const getStorageKey = (uid: string, workspaceId: string): string =>
  `${STORAGE_PREFIX}${uid}:${workspaceId}`

type StoreListener = () => void
const listeners = new Set<StoreListener>()
const memoryCache = new Map<string, CountDraft[]>()

const notifyListeners = () => {
  for (const listener of Array.from(listeners)) {
    try {
      listener()
    } catch {
      // ignore
    }
  }
}

const EMPTY_DRAFTS: CountDraft[] = []

export const _resetDraftStoreForTesting = (): void => {
  memoryCache.clear()
  listeners.clear()
}

export const getCountDrafts = (uid: string, workspaceId: string): CountDraft[] => {
  if (!uid || !workspaceId) return EMPTY_DRAFTS
  const key = getStorageKey(uid, workspaceId)
  const cached = memoryCache.get(key)
  if (cached) return cached
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      memoryCache.set(key, EMPTY_DRAFTS)
      return EMPTY_DRAFTS
    }
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      memoryCache.set(key, EMPTY_DRAFTS)
      return EMPTY_DRAFTS
    }
    const parsed = JSON.parse(raw)
    const result = Array.isArray(parsed) ? (parsed as CountDraft[]) : EMPTY_DRAFTS
    memoryCache.set(key, result)
    return result
  } catch {
    memoryCache.set(key, EMPTY_DRAFTS)
    return EMPTY_DRAFTS
  }
}

export const setCountDrafts = (
  uid: string,
  workspaceId: string,
  drafts: CountDraft[],
): void => {
  if (!uid || !workspaceId) return
  const key = getStorageKey(uid, workspaceId)
  memoryCache.set(key, drafts)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (drafts.length === 0) {
        window.localStorage.removeItem(key)
      } else {
        window.localStorage.setItem(key, JSON.stringify(drafts))
      }
    }
  } catch {
    // try/catch around storage
  }
  notifyListeners()
}

export const clearCountDrafts = (uid: string, workspaceId: string): void => {
  if (!uid || !workspaceId) return
  const key = getStorageKey(uid, workspaceId)
  memoryCache.set(key, EMPTY_DRAFTS)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key)
    }
  } catch {
    // try/catch around storage
  }
  notifyListeners()
}

export const clearAllCountDrafts = (): void => {
  memoryCache.clear()
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i)
        if (k && k.startsWith(STORAGE_PREFIX)) {
          keysToRemove.push(k)
        }
      }
      for (const k of keysToRemove) {
        window.localStorage.removeItem(k)
      }
    }
  } catch {
    // try/catch around storage
  }
  notifyListeners()
}

export const hasCountDrafts = (uid: string, workspaceId: string): boolean => {
  return getCountDrafts(uid, workspaceId).length > 0
}

export const subscribeCountDrafts = (listener: StoreListener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const useCountDrafts = (
  uid: string | null | undefined,
  workspaceId: string | null | undefined,
): [CountDraft[], (next: CountDraft[] | ((prev: CountDraft[]) => CountDraft[])) => void, () => void] => {
  const getSnapshot = useCallback(() => {
    if (!uid || !workspaceId) return EMPTY_DRAFTS
    return getCountDrafts(uid, workspaceId)
  }, [uid, workspaceId])

  const drafts = useSyncExternalStore(
    subscribeCountDrafts,
    getSnapshot,
    () => EMPTY_DRAFTS,
  )

  const setDrafts = useCallback(
    (next: CountDraft[] | ((prev: CountDraft[]) => CountDraft[])) => {
      if (!uid || !workspaceId) return
      const current = getCountDrafts(uid, workspaceId)
      const updated = typeof next === 'function' ? next(current) : next
      setCountDrafts(uid, workspaceId, updated)
    },
    [uid, workspaceId],
  )

  const clear = useCallback(() => {
    if (!uid || !workspaceId) return
    clearCountDrafts(uid, workspaceId)
  }, [uid, workspaceId])

  return [drafts, setDrafts, clear]
}
